// ONE PAGE AT A TIME over a cursor read (design-system §3.17 rev-7). A list
// that can pass 50 rows shows one server page and moves by Previous and Next,
// and never folds every page it has read into one growing list. A cursor read
// cannot know its total, so a page's first row number is known only from the
// pages read before it, and the last page only when the server says no
// cursor follows.
//
// Two shapes of read reach these lists. A read of one page per query
// (`useX(query, cursor)`) keeps a STACK of the cursors it walked. An infinite
// query's loaded pages are the stack already, so its screen keeps only the
// index of the page it shows. Both are pure here and unit-tested; the two
// hooks at the bottom only hold the state and reset it when the list's filter
// changes. Written for the messaging lists (spec 17 §4.1-§4.3) and shared with
// the sales books since D-560.

import { useCallback, useLayoutEffect, useRef, useState } from "react";

/** The page a list shows, in the words its pager needs. */
export interface PageWindow<T> {
  readonly items: readonly T[];
  /** 1-based page number. */
  readonly page: number;
  /** 1-based number of the first row shown; 0 when the page is empty. */
  readonly from: number;
  /** 1-based number of the last row shown; 0 when the page is empty. */
  readonly to: number;
  readonly hasPrev: boolean;
  readonly hasNext: boolean;
}

// ---------------------------------------------------------------------------
// the cursor stack (one page per query)
// ---------------------------------------------------------------------------

/** One page walked: the cursor that reads it and the rows before it. */
export interface CursorStep {
  readonly cursor: string | undefined;
  readonly offset: number;
}

export const FIRST_STEP: readonly CursorStep[] = [{ cursor: undefined, offset: 0 }];

/** The step on top of the stack: the page shown. */
export function currentStep(stack: readonly CursorStep[]): CursorStep {
  return stack[stack.length - 1] ?? { cursor: undefined, offset: 0 };
}

/** Walks to the next page. `shown` is how many rows the page on top holds. */
export function pushStep(
  stack: readonly CursorStep[],
  nextCursor: string,
  shown: number,
): readonly CursorStep[] {
  const top = currentStep(stack);
  return [...stack, { cursor: nextCursor, offset: top.offset + shown }];
}

/** Walks back one page; the first page stays put. */
export function popStep(stack: readonly CursorStep[]): readonly CursorStep[] {
  return stack.length > 1 ? stack.slice(0, -1) : stack;
}

/** The window over the page on top of the stack. */
export function stackWindow<T>(
  stack: readonly CursorStep[],
  items: readonly T[],
  nextCursor: string | null | undefined,
): PageWindow<T> {
  const top = currentStep(stack);
  return {
    items,
    page: Math.max(stack.length, 1),
    from: items.length === 0 ? 0 : top.offset + 1,
    to: items.length === 0 ? 0 : top.offset + items.length,
    hasPrev: stack.length > 1,
    hasNext: nextCursor !== null && nextCursor !== undefined && items.length > 0,
  };
}

// ---------------------------------------------------------------------------
// the infinite query (its loaded pages are the stack)
// ---------------------------------------------------------------------------

/**
 * The window over loaded page `wanted` (0-based). It is clamped to the pages
 * loaded, so asking for a page still on its way shows the last one read until
 * it lands, and it steps back over empty pages, so a list whose last page was
 * emptied (another manager released the mail on it) shows the page before
 * rather than an empty one.
 */
export function loadedWindow<T>(
  pages: readonly { readonly items: readonly T[] }[],
  wanted: number,
  moreOnServer: boolean,
): PageWindow<T> & { readonly index: number } {
  if (pages.length === 0) {
    return { items: [], index: 0, page: 1, from: 0, to: 0, hasPrev: false, hasNext: false };
  }
  let index = Math.min(Math.max(Math.trunc(wanted), 0), pages.length - 1);
  while (index > 0 && (pages[index]?.items.length ?? 0) === 0) index -= 1;
  const items = pages[index]?.items ?? [];
  let offset = 0;
  for (let i = 0; i < index; i += 1) offset += pages[i]?.items.length ?? 0;
  const laterLoaded = pages.slice(index + 1).some((p) => p.items.length > 0);
  return {
    items,
    index,
    page: index + 1,
    from: items.length === 0 ? 0 : offset + 1,
    to: items.length === 0 ? 0 : offset + items.length,
    hasPrev: index > 0,
    hasNext: items.length > 0 && (laterLoaded || moreOnServer),
  };
}

// ---------------------------------------------------------------------------
// the hooks
// ---------------------------------------------------------------------------

/** The cursor stack of a one-page-per-query list, back to page 1 whenever
 * `resetKey` (its tab and search) changes. */
export function useCursorStack(resetKey: string) {
  const [stack, setStack] = useState<readonly CursorStep[]>(FIRST_STEP);
  const lastKey = useRef(resetKey);
  let shown = stack;
  if (lastKey.current !== resetKey) {
    lastKey.current = resetKey;
    shown = FIRST_STEP;
    setStack(FIRST_STEP);
  }
  const next = useCallback((nextCursor: string, rowsShown: number) => {
    setStack((s) => pushStep(s, nextCursor, rowsShown));
  }, []);
  const prev = useCallback(() => {
    setStack(popStep);
  }, []);
  return { stack: shown, cursor: currentStep(shown).cursor, next, prev };
}

/** What a one-page-per-query list's read says about the page on screen. */
export interface StackRead<T> {
  readonly items: readonly T[];
  readonly nextCursor: string | null | undefined;
  /** True while the page asked for is still being read and an older one shows. */
  readonly turning: boolean;
  /**
   * True once the rows shown are this cursor's own read and nothing is reading
   * them again: no placeholder, no fetch in flight. Only a settled page is
   * believed when it comes back empty.
   */
  readonly settled: boolean;
  /**
   * How many of `items` the reader still sees, for a screen that hides rows
   * locally before the server catches up (a review decided here). Defaults to
   * `items.length`.
   */
  readonly visible?: number;
}

/**
 * The window over the rows on screen, from the stack that read them, and the
 * step back off an emptied page.
 *
 * While the next page is read the query keeps the page shown
 * (`keepPageOnTurn`), so the window is taken from the stack that read those
 * rows and its range stays put until the new rows land.
 *
 * A page past the first can empty under the reader: every task on page 2
 * completed, every review on it decided, every customer on it archived. Its
 * refetch then comes back with no rows, and a screen that draws its table and
 * pager only for rows would say "nothing here" while page 1 still holds 50,
 * with no control left to get back. So a settled empty page past the first
 * steps back one page, and again while the page before is empty too, which is
 * what `loadedWindow` does for an infinite query. It runs in a layout effect,
 * so the empty page is never painted.
 */
export function useStackWindow<T>(
  pages: { readonly stack: readonly CursorStep[]; readonly prev: () => void },
  read: StackRead<T>,
): PageWindow<T> {
  const held = useRef(pages.stack);
  if (!read.turning) held.current = pages.stack;
  const emptied =
    read.settled &&
    !read.turning &&
    (read.visible ?? read.items.length) === 0 &&
    pages.stack.length > 1;
  const { prev, stack } = pages;
  useLayoutEffect(() => {
    if (emptied) prev();
  }, [emptied, prev, stack]);
  return stackWindow(held.current, read.items, read.nextCursor);
}

/** What `useLoadedPager` needs from an infinite query. */
export interface InfiniteRead<T> {
  readonly data?: { readonly pages: readonly { readonly items: readonly T[] }[] } | undefined;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
}

/**
 * One page of an infinite query at a time. Next reads the following page when
 * it is not loaded yet, and the window stays on the page shown until it lands.
 * `resetKey` puts the list back on page 1.
 */
export function useLoadedPager<T>(read: InfiniteRead<T>, resetKey: string) {
  const [wanted, setWanted] = useState(0);
  const lastKey = useRef(resetKey);
  let target = wanted;
  if (lastKey.current !== resetKey) {
    lastKey.current = resetKey;
    target = 0;
    setWanted(0);
  }
  const view = loadedWindow(read.data?.pages ?? [], target, read.hasNextPage);
  return {
    view,
    /** True while the page asked for is still being read. */
    turning: read.isFetchingNextPage && target > view.index,
    prev: () => {
      setWanted(Math.max(view.index - 1, 0));
    },
    next: () => {
      const loaded = read.data?.pages.length ?? 0;
      const goal = view.index + 1;
      setWanted(goal);
      if (goal >= loaded && read.hasNextPage && !read.isFetchingNextPage) {
        void read.fetchNextPage();
      }
    },
  };
}

/** A page read that came back with no rows. */
function isEmptyPage(data: unknown): boolean {
  if (typeof data !== "object" || data === null || !("items" in data)) return false;
  const { items } = data as { readonly items: unknown };
  return Array.isArray(items) && items.length === 0;
}

/**
 * `placeholderData` for a one-page-per-query read whose key ends in its
 * cursor: a page turn (the same key but for the cursor) keeps the page shown
 * until the next one lands, so the list never drops to a skeleton between
 * pages, and a new filter or sort starts clean, so another query's rows are
 * never shown as this one's. Returned as a generic function so the query's
 * own data type flows through it.
 */
export function keepPageOnTurn(key: readonly unknown[]) {
  const list = JSON.stringify(key.slice(0, -1));
  return <T>(
    previous: T | undefined,
    previousQuery: { readonly queryKey: readonly unknown[] } | undefined,
  ): T | undefined => {
    if (previous === undefined || previousQuery === undefined) return undefined;
    // An emptied page is never held: the only turn away from one is the step
    // back (`useStackWindow`), and holding its no rows while the page before
    // is read would paint the false empty state the step back exists to avoid.
    if (isEmptyPage(previous)) return undefined;
    return JSON.stringify(previousQuery.queryKey.slice(0, -1)) === list ? previous : undefined;
  };
}
